import { ulid } from 'ulid';
import {
  IEntityBase,
  IMetadata,
  IProjectJavaData,
  IProjectWebConfig,
  IR2PathsWithThumbnail,
  IScriptParams,
  IThreeLayerID
} from '../types/base';
import { IFork } from '../types/fork';
import { IOwnership } from '../types/ownership';
import { ProjectCategory } from '../types/ProjectCategory';
import { ITreeStructure } from '../types/tree';
import { IVersionContainer } from '../types/version';
import { IVisibilityChild } from '../types/visibility';
import { createMergeToDefaultData } from '../utils/mergeToDefaultData';
import { createDefaultTriggerActionGroup, ITriggerActionGroup } from './ITriggerActionGroup';

/**
 * 项目接口（泛型版本）
 * 对应SQL表：project_project
 * 对应Go结构：ProjectProject
 *
 * list字段存储的是子trigger_action_group的ULID列表
 * children字段存储完整的子trigger_action_group对象列表（运行时填充）
 */
export interface IProject<
  TJavaData extends IProjectJavaData = IProjectJavaData,
  TWebConfig extends IProjectWebConfig = IProjectWebConfig
> extends
  IThreeLayerID,
  IEntityBase,
  IR2PathsWithThumbnail,
  IOwnership,
  IVisibilityChild,
  IFork,
  IVersionContainer,
  ITreeStructure<ITriggerActionGroup>,
  IMetadata,
  IScriptParams<TJavaData, TWebConfig>
{
  visibility: ProjectVisibility;   // 项目可见性
  // clientId?: string;

  /**
   * 项目的显示标题
   * 
   * 设计意图：
   * - 可以略长一些，用于面向用户的显示场景（如发布、分享、市场展示）
   * - 比 name 更正式、更完整的标题描述
   * 
   * 当前状态：
   * - 仅 IProject 使用，其他实体使用 name
   * - 与 name 存在一定重叠和混淆（历史遗留问题）
   * 
   * 未来计划：
   * - 待后续重构时明确区分：name（简短标识）vs title（较长标题）
   * - 可能扩展到其他实体类型
   */
  title?: string;

  /**
   * 项目的副标题
   * 
   * 设计意图：
   * - 可以更长一些，用于补充说明 title
   * - 提供额外的项目描述信息
   * 
   * 当前状态：
   * - 仅 IProject 使用
   */
  subtitle?: string;

  type: string;                       // 实体类型标识（必填）
  category: string;                   // 项目类别（必填）
  keepAlive?: boolean;                // 是否保持活跃

  tags?: string[];                    // 标签
  categories?: string[];              // 分类

  r2Path?: string;                    // 项目完整数据在R2的存储路径
  r2Account?: string;                 // R2账号ID（用于生成预签名URL）

  usageCount?: number;                // 被使用次数
  forkCount?: number;                 // 被fork次数

  // list字段继承自ITreeStructure，存储子trigger_action_group的uuid
  // children字段继承自ITreeStructure<ITriggerActionGroup>，存储完整对象

  // 前端使用：主 TriggerActionGroup，后端是children，前端是单个triggerActionGroup。前后端交互时需要转换，后端返回给前端的数据需要把children[0]赋值给前端的triggerActionGroup，前端给后端传递数据需要把 triggerActionGroup赋值给 children[0]。
  // 后端已经做了可以多个triggerActionGroup，但是前端技术债，所以继续保留了单个triggerActionGroup，以后需要的话，前端自己改为使用children就行。
  // triggerActionGroup: ITriggerActionGroup;
}

// 向后兼容的默认类型
export type IProjectDefault = IProject<IProjectJavaData, IProjectWebConfig>;

// === 类型常量 ===
export const projectType = 'project'

/**
 * 项目可见性枚举
 */
export enum ProjectVisibility {
  Private = 'private',
  Public = 'public'
}

// === Project Utils ===
/**
 * 创建默认的Project对象
 * 自动创建默认的 TriggerActionGroup
 *
 * 注意: 前端创建时只有 ulid,uuid 由后端生成
 * list 存储子 trigger_action_group 的 ulid
 */
export function createDefaultProject(): IProject {
  const triggerActionGroup = createDefaultTriggerActionGroup();
  return {
    ulid: ulid(),
    uuid: '',
    ownerUuid: '',
    currentVersion: 1,
    type: projectType,
    category: '',
    list: [],
    children: [triggerActionGroup],
    createdAt: Date.now(),
    updatedAt: Date.now(),
    authorUuid: '',
    visibility: ProjectVisibility.Private,
    childrenVersion: 1
  } as IProject
}

/**
 * 根据 ProjectCategory 创建 Project 默认数据
 * @param category 项目类别
 */
export function createProjectByCategory(category: ProjectCategory): IProject {
  const project = createDefaultProject();
  project.category = category;
  return project;
}

export interface IProjectUtils {
  type: string
  createDefaultData: () => IProject
  mergeDataToDefaultData: (data: Partial<IProject>) => IProject
}

export const projectUtils: IProjectUtils = {
  type: projectType,
  createDefaultData: createDefaultProject,
  mergeDataToDefaultData: createMergeToDefaultData(createDefaultProject)
}
